import React, { useEffect, useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { 
  Button, 
  TextField, 
  Box, 
  Typography, 
  Select, 
  MenuItem, 
  InputLabel, 
  FormControl,
  Grid,
  Paper,
  FormHelperText
} from '@mui/material';
import { createBackup, scheduleBackup } from '../../services/backupService';
import { getHosts } from '../../services/hostService';
import { useNavigate } from 'react-router-dom';

const BackupForm = ({ scheduleMode = false }) => {
  const navigate = useNavigate();
  const [hosts, setHosts] = useState([]);
  const [frequency, setFrequency] = useState('daily');
  const [time, setTime] = useState('00:00');
  const [dayOfWeek, setDayOfWeek] = useState('1');
  const [dayOfMonth, setDayOfMonth] = useState('1');

  useEffect(() => {
    const fetchHosts = async () => {
      try {
        const hostsData = await getHosts();
        setHosts(hostsData);
      } catch (error) {
        console.error('Error fetching hosts:', error);
      }
    };

    fetchHosts();
  }, []);

  const frequencyOptions = [
    { value: 'hourly', label: 'A cada hora' },
    { value: 'daily', label: 'Diariamente' },
    { value: 'weekly', label: 'Semanalmente' },
    { value: 'monthly', label: 'Mensalmente' },
  ];

  const weekDays = [
    { value: '0', label: 'Domingo' },
    { value: '1', label: 'Segunda-feira' },
    { value: '2', label: 'Terça-feira' },
    { value: '3', label: 'Quarta-feira' },
    { value: '4', label: 'Quinta-feira' },
    { value: '5', label: 'Sexta-feira' },
    { value: '6', label: 'Sábado' },
  ];

  const convertToCron = () => {
    const [hours, minutes] = time.split(':');
    
    switch(frequency) {
      case 'hourly':
        return `0 * * * *`; // A cada hora no minuto 0
      case 'daily':
        return `${minutes} ${hours} * * *`; // Diário no horário especificado
      case 'weekly':
        return `${minutes} ${hours} * * ${dayOfWeek}`; // Semanal no dia especificado
      case 'monthly':
        return `${minutes} ${hours} ${dayOfMonth} * *`; // Mensal no dia especificado
      default:
        return `0 0 * * *`; // Padrão: meia-noite diariamente
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      let backupData = { host: values.host };

      if (scheduleMode) {
        backupData.cronExpression = convertToCron();
        await scheduleBackup(backupData); // ✅ Chamada correta
      } else {
        await createBackup(backupData); // ✅ Apenas se for manual
      }

      navigate('/backups');
    } catch (error) {
      console.error('Error creating backup:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const initialValues = {
    host: '',
  };

  const validationSchema = Yup.object().shape({
    host: Yup.string().required('Selecione um host'),
  });

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        {scheduleMode ? 'Agendar Backup Automático' : 'Criar Backup Manual'}
      </Typography>
      
      <Paper elevation={3} sx={{ p: 3 }}>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, values, handleChange }) => (
            <Form>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Host</InputLabel>
                    <Field
                      as={Select}
                      name="host"
                      label="Host"
                      value={values.host}
                      onChange={handleChange}
                    >
                      {hosts.map((host) => (
                        <MenuItem key={host._id} value={host._id}>
                          {host.name} ({host.mongoUri})
                        </MenuItem>
                      ))}
                    </Field>
                    <ErrorMessage name="host" component={FormHelperText} error />
                  </FormControl>
                </Grid>

                {scheduleMode && (
                  <>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Frequência</InputLabel>
                        <Select
                          value={frequency}
                          onChange={(e) => setFrequency(e.target.value)}
                          label="Frequência"
                        >
                          {frequencyOptions.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    {frequency !== 'hourly' && (
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Horário"
                          type="time"
                          value={time}
                          onChange={(e) => setTime(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    )}

                    {frequency === 'weekly' && (
                      <Grid item xs={12}>
                        <FormControl fullWidth>
                          <InputLabel>Dia da Semana</InputLabel>
                          <Select
                            value={dayOfWeek}
                            onChange={(e) => setDayOfWeek(e.target.value)}
                            label="Dia da Semana"
                          >
                            {weekDays.map(day => (
                              <MenuItem key={day.value} value={day.value}>
                                {day.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    )}

                    {frequency === 'monthly' && (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Dia do Mês"
                          type="number"
                          value={dayOfMonth}
                          onChange={(e) => setDayOfMonth(e.target.value)}
                          inputProps={{ min: 1, max: 31 }}
                        />
                      </Grid>
                    )}
                  </>
                )}

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button 
                      type="submit" 
                      variant="contained" 
                      size="large"
                      disabled={isSubmitting}
                    >
                      {scheduleMode ? 'Agendar Backup' : 'Criar Backup'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Paper>

      {scheduleMode && (
        <Box sx={{ mt: 4, p: 3, bgcolor: 'background.paper', borderRadius: 1 }}>
          <Typography variant="h6" gutterBottom>
            Como funciona o agendamento?
          </Typography>
          <Typography paragraph>
            O backup será executado automaticamente conforme a programação definida.
          </Typography>
          <Typography paragraph>
            <strong>Backups agendados ficam ativos continuamente</strong> até serem removidos manualmente.
          </Typography>
          <Typography>
            Você pode visualizar e gerenciar os agendamentos ativos na página de Backups.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default BackupForm;